import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@repo/blacksmith-ui/dialog";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useIsMac } from "@/hooks/useIsMac";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useActiveApp } from "@/components/sidebar/useActiveApp";

interface ShortcutsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Shortcut {
  keys: string[];
  description: string;
  alt?: string[];
}

interface ShortcutSection {
  category: string;
  items: Shortcut[];
}

const getShortcuts = (
  isMac: boolean,
  {
    includeTests,
    includeCodesmith,
    includeRailLetters,
  }: {
    includeTests: boolean;
    includeCodesmith: boolean;
    includeRailLetters: boolean;
  },
): ShortcutSection[] => {
  const mod = isMac ? "Cmd" : "Ctrl";

  const sections: ShortcutSection[] = [
    {
      category: "Navigation",
      items: [
        ...(includeRailLetters
          ? [
              { keys: ["h"], description: "Home" },
              { keys: ["a"], description: "GitHub Actions" },
            ]
          : []),
        ...(includeRailLetters && includeCodesmith
          ? [{ keys: ["p"], description: "Proof" }]
          : []),
        { keys: ["g", "w"], description: "Workflows" },
        { keys: ["g", "j"], description: "Jobs" },
        { keys: ["g", "l"], description: "Logs" },
        { keys: ["g", "a"], description: "Analytics" },
        { keys: ["g", "d"], description: "Docker Analytics" },
        { keys: ["g", "s"], description: "Sticky Disks" },
        { keys: ["g", "m"], description: "Monitors" },
        { keys: ["g", "u"], description: "Usage & Billing" },
        { keys: ["g", "t"], description: "Settings" },
      ],
    },
  ];

  if (includeTests) {
    sections.push({
      category: "Tests",
      items: [
        { keys: ["j"], description: "Next test/suite", alt: ["↓"] },
        { keys: ["k"], description: "Previous test/suite", alt: ["↑"] },
        {
          keys: ["Enter"],
          description: "Select test or toggle suite",
          alt: ["←"],
        },
        { keys: ["/"], description: "Focus search" },
        { keys: ["f"], description: "Open filter menu" },
      ],
    });
  }

  sections.push({
    category: "Interface",
    items: [
      { keys: [mod, "K"], description: "Open command palette" },
      { keys: [mod, "B"], description: "Toggle sidebar" },
      { keys: [mod, "/"], description: "Toggle all panels" },
      { keys: ["?"], description: "Show shortcuts" },
    ],
  });

  if (includeCodesmith) {
    sections.push({
      category: "[code]smith",
      items: [
        ...(includeRailLetters
          ? [{ keys: ["c"], description: "Open [code]smith" }]
          : []),
        {
          keys: [isMac ? "Option" : "Alt", "Shift", "N"],
          description: "New chat",
        },
        {
          keys: [isMac ? "Option" : "Alt", "B"],
          description: "Toggle chat list",
        },
        {
          keys: [isMac ? "Option" : "Alt", mod, "B"],
          description: "Toggle workspace panel",
        },
        {
          keys: [isMac ? "Option" : "Alt", "L"],
          description: "Focus chat input",
        },
        {
          // A single "Option ×2" badge: two badges would render as
          // "Option + Option", which reads as a simultaneous two-key chord.
          keys: [isMac ? "Option ×2" : "Alt ×2"],
          description:
            "Session switcher (double-tap and hold, J/K to move, release to switch)",
          alt: [isMac ? "Option" : "Alt", "J/K"],
        },
        {
          keys: [isMac ? "Option" : "Alt", "D"],
          description: "Archive current session",
        },
        {
          keys: [isMac ? "Option" : "Alt", "G"],
          description: "Open session's PRs on GitHub",
        },
        {
          keys: [isMac ? "Option" : "Alt", "P"],
          description: "Open session's PR preview environments",
        },
        {
          keys: ["Shift", mod, "Enter"],
          description: "Queue message (Enter steers while [code]smith is busy)",
          alt: [isMac ? "Option" : "Alt", "Shift", "Enter"],
        },
        {
          keys: [mod, "Shift", "."],
          description: "Toggle Fast/Deep mode",
        },
      ],
    });
  }

  return sections;
};

function KeyBadges({ keys }: { keys: string[] }) {
  return <ShortcutKeys keys={keys} kbdClassName="h-5 min-w-5" />;
}

export function ShortcutsModal({ open, onOpenChange }: ShortcutsModalProps) {
  const isMac = useIsMac();
  const router = useRouter();
  const activeApp = useActiveApp();

  const { codesmithEnabled } = useActiveGithubOrgForUserNonSuspense();
  const isTestPage = router.pathname.includes("[runId]");
  const shortcuts = getShortcuts(isMac, {
    includeTests: isTestPage,
    includeCodesmith: codesmithEnabled ?? false,
    // The rail's bare-letter shortcuts are inert inside [code]smith.
    includeRailLetters: activeApp !== "codesmith",
  });

  const leftColumn = shortcuts.filter(
    (s) => s.category === "Navigation" || s.category === "Tests",
  );
  const rightColumn = shortcuts.filter(
    (s) => s.category === "Interface" || s.category === "[code]smith",
  );

  const renderSection = (section: ShortcutSection) => (
    <div key={section.category}>
      <h3 className="text-sm font-medium text-muted-foreground mb-3">
        {section.category}
      </h3>
      <div className="space-y-2">
        {section.items.map((shortcut, index) => (
          <div
            key={index}
            className="flex items-start justify-between gap-x-4 py-1"
          >
            <span className="min-w-0 whitespace-pre-line text-sm">
              {shortcut.description}
            </span>
            <div className="flex flex-wrap items-center justify-end gap-1">
              <KeyBadges keys={shortcut.keys} />
              {shortcut.alt && (
                <span className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground mx-1">or</span>
                  <KeyBadges keys={shortcut.alt} />
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-10">
          <div className="space-y-6">{leftColumn.map(renderSection)}</div>
          <div className="space-y-6">{rightColumn.map(renderSection)}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
